import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import { ConfirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import {
  confirmDeleteCategory,
  confirmDeleteSubCategory,
  createOrUpdateCategory,
  createOrUpdateSubCategory,
  getAssetCategories,
  getAllSubCategoriesGrouped,
  getSubCategoriesByCategory,
} from "./services/AssetCategoryService";

const AssetCategories = () => {
  document.title = "Asset Categories | Valitan IMS";

  const [categories, setCategories] = useState([]);
  const [subCategoriesByCategory, setSubCategoriesByCategory] = useState({});
  const [loadingSubCategories, setLoadingSubCategories] = useState({});
  const [loadingCategoryPage, setLoadingCategoryPage] = useState(false);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [subCategoryModalOpen, setSubCategoryModalOpen] = useState(false);
  const [viewSubCategoriesModalOpen, setViewSubCategoriesModalOpen] = useState(false);

  const [categoryName, setCategoryName] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryErrors, setCategoryErrors] = useState({ name: "", code: "" });
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  const [subCategoryName, setSubCategoryName] = useState("");
  const [subCategoryCode, setSubCategoryCode] = useState("");
  const [subCategoryErrors, setSubCategoryErrors] = useState({ name: "", code: "" });
  const [editingSubCategoryId, setEditingSubCategoryId] = useState(null);
  const [activeParentCategory, setActiveParentCategory] = useState(null);
  const [viewingCategory, setViewingCategory] = useState(null);
  const [subCategorySearch, setSubCategorySearch] = useState("");

  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10,
    sort: {},
  });

  const loadCategories = useCallback(async (page = 0, size = paginationInfo.pageSize) => {
    setLoadingCategoryPage(true);
    try {
      const [{ items, paginationInfo: info }, groupedSubCategories] = await Promise.all([
        getAssetCategories(page, size),
        getAllSubCategoriesGrouped(),
      ]);
      setCategories(items || []);
      setSubCategoriesByCategory(groupedSubCategories || {});
      setPaginationInfo(
        info || {
          totalElements: 0,
          totalPages: 0,
          pageNumber: 0,
          pageSize: size,
          sort: {},
        }
      );
    } finally {
      setLoadingCategoryPage(false);
    }
  }, [paginationInfo.pageSize]);

  const loadSubCategories = useCallback(async (categoryId) => {
    setLoadingSubCategories((prev) => ({ ...prev, [categoryId]: true }));
    const items = await getSubCategoriesByCategory(categoryId);
    setSubCategoriesByCategory((prev) => ({ ...prev, [categoryId]: items }));
    setLoadingSubCategories((prev) => ({ ...prev, [categoryId]: false }));
    return items;
  }, []);

  const refreshSubCategoryCounts = useCallback(async () => {
    const groupedSubCategories = await getAllSubCategoriesGrouped();
    setSubCategoriesByCategory(groupedSubCategories || {});
  }, []);

  useEffect(() => {
    loadCategories(0, paginationInfo.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.pageSize]);

  const viewingSubCategories = viewingCategory
    ? subCategoriesByCategory[viewingCategory.id] || []
    : [];

  const filteredViewingSubCategories = useMemo(() => {
    const query = subCategorySearch.trim().toLowerCase();
    if (!query) return viewingSubCategories;
    return viewingSubCategories.filter(
      (item) =>
        item.name?.toLowerCase().includes(query) ||
        item.code?.toLowerCase().includes(query)
    );
  }, [viewingSubCategories, subCategorySearch]);

  const openViewSubCategoriesModal = async (category) => {
    setViewingCategory(category);
    setSubCategorySearch("");
    setViewSubCategoriesModalOpen(true);
    if (!subCategoriesByCategory[category.id]) {
      await loadSubCategories(category.id);
    }
  };

  const closeViewSubCategoriesModal = () => {
    setViewSubCategoriesModalOpen(false);
    setViewingCategory(null);
    setSubCategorySearch("");
  };

  const toggleCategoryModal = () => {
    setCategoryModalOpen((prev) => {
      if (prev) {
        setCategoryName("");
        setCategoryCode("");
        setCategoryDescription("");
        setCategoryErrors({ name: "", code: "" });
        setEditingCategoryId(null);
      }
      return !prev;
    });
  };

  const openCategoryModalForAdd = () => {
    setCategoryName("");
    setCategoryCode("");
    setCategoryDescription("");
    setCategoryErrors({ name: "", code: "" });
    setEditingCategoryId(null);
    setCategoryModalOpen(true);
  };

  const openCategoryModalForEdit = (category) => {
    setEditingCategoryId(category.id);
    setCategoryName(category.name ?? "");
    setCategoryCode(category.code ?? "");
    setCategoryDescription(category.description ?? "");
    setCategoryErrors({ name: "", code: "" });
    setCategoryModalOpen(true);
  };

  const toggleSubCategoryModal = () => {
    setSubCategoryModalOpen((prev) => {
      if (prev) {
        setSubCategoryName("");
        setSubCategoryCode("");
        setSubCategoryErrors({ name: "", code: "" });
        setEditingSubCategoryId(null);
        setActiveParentCategory(null);
      }
      return !prev;
    });
  };

  const openSubCategoryModalForAdd = (category) => {
    setActiveParentCategory(category);
    setSubCategoryName("");
    setSubCategoryCode("");
    setSubCategoryErrors({ name: "", code: "" });
    setEditingSubCategoryId(null);
    setSubCategoryModalOpen(true);
  };

  const openSubCategoryModalForEdit = (category, subCategory) => {
    setActiveParentCategory(category);
    setEditingSubCategoryId(subCategory.id);
    setSubCategoryName(subCategory.name ?? "");
    setSubCategoryCode(subCategory.code ?? "");
    setSubCategoryErrors({ name: "", code: "" });
    setSubCategoryModalOpen(true);
  };

  const handleSubCategorySaved = async () => {
    await refreshSubCategoryCounts();
    if (activeParentCategory?.id && viewingCategory?.id === activeParentCategory.id) {
      setViewingCategory(activeParentCategory);
    }
  };

  const handleSubCategoryDeleted = async () => {
    await refreshSubCategoryCounts();
  };

  const handleNextPage = () => {
    if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
      loadCategories(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.pageNumber > 0) {
      loadCategories(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
    }
  };

  const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Settings" breadcrumbItem="Asset Categories" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <h4 className="card-title mb-0">Asset Categories & Sub-categories</h4>
                  <Button color="primary" onClick={openCategoryModalForAdd}>
                    <i className="ri-add-line align-bottom me-1"></i> Add Category
                  </Button>
                </CardHeader>

                <CardBody>
                  <div className="table-responsive table-card mt-1 mb-1">
                    <table className="table align-middle table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Description</th>
                          <th>Sub-categories</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.length > 0 ? (
                          categories.map((category, index) => {
                            const subCount = subCategoriesByCategory[category.id]?.length ?? 0;

                            return (
                              <tr key={category.id}>
                                <td>{startIndex + index}</td>
                                <td>{category.name}</td>
                                <td>
                                  <span className="badge bg-soft-primary text-primary">{category.code}</span>
                                </td>
                                <td>{category.description || "—"}</td>
                                <td>
                                  {loadingCategoryPage ? (
                                    <span className="text-muted">...</span>
                                  ) : (
                                    <span className={subCount > 0 ? "fw-medium" : "text-muted"}>{subCount}</span>
                                  )}
                                </td>
                                <td>
                                  <div className="btn-group" role="group">
                                    <button
                                      type="button"
                                      className="btn btn-info btn-sm"
                                      title="View sub-categories"
                                      onClick={() => openViewSubCategoriesModal(category)}
                                    >
                                      <i className="ri-eye-line"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-success btn-sm"
                                      title="Add sub-category"
                                      onClick={() => openSubCategoryModalForAdd(category)}
                                    >
                                      <i className="ri-add-line"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      title="Edit category"
                                      onClick={() => openCategoryModalForEdit(category)}
                                    >
                                      <i className="ri-edit-box-line"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      title="Delete category"
                                      onClick={() =>
                                        confirmDeleteCategory(category.id, () =>
                                          loadCategories(paginationInfo.pageNumber, paginationInfo.pageSize)
                                        )
                                      }
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={6} className="text-center">
                              No categories found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    paginationInfo={paginationInfo}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    onPageChange={(pageNumber, pageSize) => loadCategories(pageNumber, pageSize)}
                    onPageSizeChange={(newPageSize) => {
                      setPaginationInfo((prev) => ({
                        ...prev,
                        pageSize: newPageSize,
                        pageNumber: 0,
                      }));
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ConfirmDialog />

      <Modal isOpen={viewSubCategoriesModalOpen} toggle={closeViewSubCategoriesModal} size="lg">
        <ModalHeader toggle={closeViewSubCategoriesModal}>
          Sub-categories — {viewingCategory?.name}
          {viewingCategory?.code ? (
            <span className="badge bg-soft-primary text-primary ms-2">{viewingCategory.code}</span>
          ) : null}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <p className="text-muted mb-0">
              {loadingSubCategories[viewingCategory?.id]
                ? "Loading..."
                : `${viewingSubCategories.length} sub-categor${viewingSubCategories.length === 1 ? "y" : "ies"}`}
            </p>
            <div className="d-flex flex-wrap gap-2">
              <Input
                type="search"
                placeholder="Search sub-categories..."
                value={subCategorySearch}
                onChange={(e) => setSubCategorySearch(e.target.value)}
                style={{ minWidth: 220 }}
              />
              <Button
                color="success"
                size="sm"
                onClick={() => viewingCategory && openSubCategoryModalForAdd(viewingCategory)}
              >
                <i className="ri-add-line me-1"></i> Add Sub-category
              </Button>
            </div>
          </div>

          {loadingSubCategories[viewingCategory?.id] ? (
            <p className="text-muted text-center py-4 mb-0">Loading sub-categories...</p>
          ) : filteredViewingSubCategories.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-bordered table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>Name</th>
                    <th>Code</th>
                    <th style={{ width: 120 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredViewingSubCategories.map((sub, index) => (
                    <tr key={sub.id}>
                      <td>{index + 1}</td>
                      <td>{sub.name}</td>
                      <td>
                        <span className="badge bg-soft-secondary text-secondary">{sub.code || "—"}</span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            type="button"
                            className="btn btn-primary"
                            title="Edit sub-category"
                            onClick={() => openSubCategoryModalForEdit(viewingCategory, sub)}
                          >
                            <i className="ri-edit-box-line"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger"
                            title="Delete sub-category"
                            onClick={() =>
                              confirmDeleteSubCategory(sub.id, handleSubCategoryDeleted)
                            }
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted text-center py-4 mb-0">
              {subCategorySearch.trim()
                ? "No sub-categories match your search."
                : "No sub-categories for this category yet."}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeViewSubCategoriesModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={categoryModalOpen} toggle={toggleCategoryModal} backdrop="static">
        <ModalHeader toggle={toggleCategoryModal}>
          {editingCategoryId ? "Edit Category" : "Add Category"}
        </ModalHeader>
        <ModalBody>
          <form
            onSubmit={(event) =>
              createOrUpdateCategory(
                event,
                {
                  name: categoryName,
                  code: categoryCode,
                  description: categoryDescription,
                },
                editingCategoryId,
                () => loadCategories(paginationInfo.pageNumber, paginationInfo.pageSize),
                toggleCategoryModal,
                setCategoryErrors
              )
            }
          >
            <FormGroup>
              <Label for="categoryName">Name</Label>
              <Input
                id="categoryName"
                type="text"
                placeholder="Enter category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                invalid={!!categoryErrors.name}
              />
              {categoryErrors.name && (
                <div className="invalid-feedback d-block">{categoryErrors.name}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="categoryCode">Code</Label>
              <Input
                id="categoryCode"
                type="text"
                placeholder="e.g. BLG, EQP"
                value={categoryCode}
                onChange={(e) => setCategoryCode(e.target.value.toUpperCase())}
                invalid={!!categoryErrors.code}
              />
              {categoryErrors.code && (
                <div className="invalid-feedback d-block">{categoryErrors.code}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="categoryDescription">Description (optional)</Label>
              <Input
                id="categoryDescription"
                type="textarea"
                rows={3}
                placeholder="Enter description"
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
              />
            </FormGroup>

            <Button type="submit" color="primary" className="float-end">
              {editingCategoryId ? "Update" : "Save"}
            </Button>
          </form>
        </ModalBody>
      </Modal>

      <Modal isOpen={subCategoryModalOpen} toggle={toggleSubCategoryModal} backdrop="static">
        <ModalHeader toggle={toggleSubCategoryModal}>
          {editingSubCategoryId ? "Edit Sub-category" : "Add Sub-category"}
        </ModalHeader>
        <ModalBody>
          <form
            onSubmit={(event) =>
              createOrUpdateSubCategory(
                event,
                {
                  name: subCategoryName,
                  code: subCategoryCode,
                  assetCategoryId: activeParentCategory?.id,
                },
                editingSubCategoryId,
                handleSubCategorySaved,
                toggleSubCategoryModal,
                setSubCategoryErrors
              )
            }
          >
            <FormGroup>
              <Label>Parent Category</Label>
              <Input type="text" value={activeParentCategory?.name || ""} disabled />
            </FormGroup>

            <FormGroup>
              <Label for="subCategoryName">Name</Label>
              <Input
                id="subCategoryName"
                type="text"
                placeholder="Enter sub-category name"
                value={subCategoryName}
                onChange={(e) => setSubCategoryName(e.target.value)}
                invalid={!!subCategoryErrors.name}
              />
              {subCategoryErrors.name && (
                <div className="invalid-feedback d-block">{subCategoryErrors.name}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="subCategoryCode">Code</Label>
              <Input
                id="subCategoryCode"
                type="text"
                placeholder="Enter code"
                value={subCategoryCode}
                onChange={(e) => setSubCategoryCode(e.target.value.toUpperCase())}
                invalid={!!subCategoryErrors.code}
              />
              {subCategoryErrors.code && (
                <div className="invalid-feedback d-block">{subCategoryErrors.code}</div>
              )}
            </FormGroup>

            <Button type="submit" color="primary" className="float-end">
              {editingSubCategoryId ? "Update" : "Save"}
            </Button>
          </form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AssetCategories;
